import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

// ─── Module-level trigger system ────────────────────────────────────────────
// Other components call triggerFeedbackWin('quiz_perfect') etc. to fire a
// feedback prompt after a meaningful win moment.
const feedbackListeners = new Set<(winType: string) => void>();

export function triggerFeedbackWin(winType: string): void {
  feedbackListeners.forEach((fn) => fn(winType));
}

// ─── Rate-limit helpers ───────────────────────────────────────────────────────
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function wasAlreadyShownForType(winType: string): boolean {
  return localStorage.getItem(`learnex_feedback_shown_${winType}`) === '1';
}

function wasShownRecently(): boolean {
  const ts = localStorage.getItem('learnex_last_feedback_ts');
  if (!ts) return false;
  return Date.now() - parseInt(ts, 10) < SEVEN_DAYS_MS;
}

function markShown(winType: string): void {
  localStorage.setItem(`learnex_feedback_shown_${winType}`, '1');
  localStorage.setItem('learnex_last_feedback_ts', String(Date.now()));
}

// ─── Types ────────────────────────────────────────────────────────────────────
type PromptStage = 'toast' | 'positive-modal' | 'negative-modal' | null;

const ff   = { fontFamily: "'Fraunces', serif" } as const;
const mono = { fontFamily: "'Space Mono', monospace", letterSpacing: '0.05em' } as const;

export default function FeedbackButton() {
  const { pathname } = useLocation();
  const { session } = useAuth();
  const { showSuccess, showError } = useToast();

  const [stage, setStage]             = useState<PromptStage>(null);
  const [message, setMessage]         = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [pendingWin, setPendingWin]   = useState<string | null>(null);

  // Only active on /app/* routes with a session
  const isActive = pathname.startsWith('/app') && !!session;

  // Register / deregister this instance as a listener
  const handleWin = useCallback((winType: string) => {
    if (!isActive) return;
    if (wasAlreadyShownForType(winType)) return;
    if (wasShownRecently()) return;
    // All checks passed — show the toast prompt
    setPendingWin(winType);
    setStage('toast');
  }, [isActive]);

  useEffect(() => {
    feedbackListeners.add(handleWin);
    return () => { feedbackListeners.delete(handleWin); };
  }, [handleWin]);

  function dismiss() {
    setStage(null);
    setMessage('');
    setPendingWin(null);
  }

  function handleThumbsUp() {
    if (pendingWin) markShown(pendingWin);
    setStage('positive-modal');
  }

  function handleThumbsDown() {
    if (pendingWin) markShown(pendingWin);
    setStage('negative-modal');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);

    const name  = session?.user?.user_metadata?.full_name  || 'Learnex User';
    const email = session?.user?.email                     || 'no-reply@learnex.cc';
    const subject = stage === 'positive-modal' ? 'Positive Feedback' : 'Improvement Feedback';

    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/contact`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, email, subject: `In-App Feedback — ${subject}`, message: message.trim() }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to send feedback');
      showSuccess('Thanks for your feedback! It means a lot. 🙌');
      dismiss();
    } catch (err: any) {
      showError(err.message || 'Could not send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!isActive) return null;

  return (
    <>
      {/* ── Win Toast Prompt ─────────────────────────────────────────────── */}
      {stage === 'toast' && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-inverse-surface text-inverse-on-surface rounded-full px-6 py-3 shadow-2xl"
          style={{ animation: 'slideUp 0.35s ease' }}
          role="status"
          aria-live="polite"
        >
          <style>{`
            @keyframes slideUp {
              from { transform: translate(-50%, 24px); opacity: 0; }
              to   { transform: translate(-50%, 0);    opacity: 1; }
            }
          `}</style>
          <span className="text-sm font-medium" style={ff}>🎉 Enjoying Learnex?</span>
          <div className="flex gap-2">
            <button
              onClick={handleThumbsUp}
              className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors"
              style={mono}
            >
              👍 Yes!
            </button>
            <button
              onClick={handleThumbsDown}
              className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 rounded-full px-4 py-1.5 text-sm transition-colors"
              style={mono}
            >
              👎
            </button>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="ml-1 w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors text-inverse-on-surface/60 hover:text-inverse-on-surface"
          >
            <span className="material-symbols-outlined text-[14px]" aria-hidden="true">close</span>
          </button>
        </div>
      )}

      {/* ── Modal (positive or negative) ────────────────────────────────── */}
      {(stage === 'positive-modal' || stage === 'negative-modal') && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
        >
          <div
            className="bg-paper-bg w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-fade-in"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-modal-title"
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-outline-variant">
              <div>
                <h2 id="feedback-modal-title" className="text-lg font-bold text-on-surface" style={ff}>
                  {stage === 'positive-modal'
                    ? "That makes us happy! 🙌"
                    : "Help us do better"}
                </h2>
                <p className="text-xs text-ink-muted mt-1">
                  {stage === 'positive-modal'
                    ? "Anything specific you'd like to share? (optional)"
                    : "What can we improve? We read every message."}
                </p>
              </div>
              <button
                onClick={dismiss}
                aria-label="Close feedback"
                className="w-8 h-8 rounded-full bg-surface-container flex items-center justify-center hover:bg-surface-container-high transition-colors ml-4 shrink-0"
              >
                <span className="material-symbols-outlined text-[18px] text-ink-muted" aria-hidden="true">close</span>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  stage === 'positive-modal'
                    ? "Tell us what you love most, or what could be even better..."
                    : "Tell us what's not working or what you wish Learnex could do..."
                }
                rows={4}
                className="w-full rounded-xl border border-outline-variant bg-surface-container-lowest px-3 py-2.5 text-sm text-on-surface placeholder:text-ink-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
                autoFocus
              />

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-primary text-on-primary font-semibold py-2.5 rounded-full text-sm hover:bg-primary/90 hover:shadow-md active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-2 border-on-primary/40 border-t-on-primary animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-[16px]" aria-hidden="true">send</span>
                      {stage === 'positive-modal' ? 'Send Feedback' : 'Send'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  className="px-4 py-2.5 rounded-full border border-outline-variant text-ink-muted text-sm hover:bg-surface-container transition-colors"
                >
                  Skip
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
